<template>
    <div class="q-select-organisation detail-page-padding">
        <div class="head">
            <div class="left">
                <arrow-button color="black" direction="left" @click="back"></arrow-button>
                <h2>{{ project.name }}</h2>
                <q-tag class="status" size="small" variation="warning">{{ $t(`projectStatus.${project.status}`) }}</q-tag>
            </div>
        </div>
        <!-- STEP 1 - SELECT ORGANISATION -->
        <div v-if="step === 1">
            <div class="search">
                <div class="panel">
                    <div class="title">
                        <div>
                            <span class="label">Stap 1</span>
                        </div>
                        <h3>Bepaal de {{ $t(organisationType.toLowerCase()) }}</h3>
                    </div>
                    <p v-if="type === 'branch'" class="mt-s">
                        Kies hieronder de vestiging die dit project uitvoert. Je kan ook de opdrachtnemer vragen dit in te vullen.
                    </p>
                    <p v-else class="mt-s">
                        Kies in deze stap welke {{ $t(organisationType.toLowerCase()) }} je aan je project wilt koppelen. Na het koppelen van een {{ $t(organisationType.toLowerCase()) }} kun je in je project bij de algemene projectgegevens nog een specifieke vestiging selecteren.
                    </p>
                    <div v-if="type === 'main'" class="">
                        <div class="input-label">
                            {{ $t(organisationType) }}
                        </div>
                        <q-input labelPosition="inside" v-model="input" placeholder="Zoek op naam, adres of KvK-nummer" @input="_search" autofocus>
                            Naam
                        </q-input>
                    </div>
                </div>
            </div>
            <div class="results" v-if="input !== '' || type === 'branch'">
                <q-table :columns="getColumns" :data="organisations" width="1024px" :loading="loading" :loadingRows="5" @filterUpdated="handleFilterUpdate">
                    <template v-slot:row="{ column, row }">
                        <span class="vertical-center" v-if="column == 'name'">
                            <div class="avatar-container">
                                <q-avatar size="small" :img="row.logo">{{ row.name.charAt(0) }}</q-avatar>
                            </div>
                            <p class="bold">{{ row.name }}</p>
                        </span>

                        
                        <div v-else-if="column === 'type'" class="type" >
                            <img 
                                :src="row.isMainBranch  ? '/statics/img/mainbranch.svg' : row.isBranch ? '/statics/img/branch.svg' :  '/statics/img/users.svg' " 
                                width="20px" 
                            />
                            <span >{{row.isMainBranch  ? 'Hoofdvestiging' : row.isBranch ? 'Nevenvestiging' :  'Rechtspersoon' }} </span>

                            
                        </div>
                         <div v-else-if="column === 'action'">
                            <q-button class="edit" variation="ghost" size="small" @click="select(row)"
                                >Kies {{ type === 'branch' ? 'vestiging' : 'organisatie' }}</q-button
                            >
                        </div>
                        <div v-else-if="column === 'source'">
                            <img 
                                :src="row.source === 'kvk' ? '/statics/img/kvk.svg' : '/statics/img/Qfact-logo-small.png'" 
                                :width= "row.source === 'kvk' ? '30px' : '20px'" 
                            />
                            
                        </div>
                        <div v-else>
                            {{ row[column] }}
                        </div>
                    </template>
                </q-table>

            </div>
            
            <!-- Show More Results Button -->
            <show-more-button 
                :show="organisations.length > 0 && hasMoreResults"
                :loading="loadingMore"
                @click="showMoreResults"
            />
            
            <p v-if="organisations.length > 0 && input !== ''" class="not-found-text mt-m pb-m">
                Kan u een bedrijf niet vinden? Verfijn dan uw zoekterm of zoek op KvK nummer.
            </p>
        </div>

        <!-- STEP 2a ALTERNATIVE: PD MIGRATED PROJECT ON  -->
        <div v-else-if="step === '2a'">
            <div class="search-pd">
                <div class="panel">
                    <div class="title">
                        <div>
                            <span class="label">Stap 2</span>
                        </div>
                        <h3>Hoe wil je de opdrachtgever koppelen?</h3>
                    </div>
                    <div class="chosen mb-m">
                        <h2>{{ chosenOrganisation.name }}</h2>
                    </div>
                    <div class="input">
                        <label for="silent" class="option-panel" style="margin-bottom:10px">
                            <q-radio 
                                id="silent"
                                name="pd-on" 
                                key="silent"
                                label="silent"
                                width="5px"
                                :value="pdInviteClientType"
                                @input="selectInviteClientType('silent')">
                            </q-radio>
                            <p><strong>Alleen opdrachtgever selecteren</strong><br>Selecteer de opdrachtgever. Je hoeft geen contactpersoon te selecteren en deelt geen gegevens. Je kunt later op ieder moment nog je opdrachtgever toevoegen via de projectinstellingen. </p>
                        </label>
                        <q-tooltip :disabled="!selectOnly" position="top" v-project-can="'admin__Value__set_value'">
                            <template #tooltip>
                                <p>Alleen beschikbaar in Better Performance projecten</p>
                            </template>
                            
                            <label for="invite" class="option-panel" :class="{ disabled: selectOnly }">
                                <q-radio 
                                    id="invite"
                                    name="pd-on" 
                                    key="invite"
                                    label="invite"
                                    width="5px"
                                    :value="pdInviteClientType"
                                    :disabled="selectOnly"
                                    @input="selectInviteClientType('invite')">
                                </q-radio>
                                <div>
                                    <p><strong>Nodig opdrachtgever uit</strong><br>Nodig de opdrachtgever uit en selecteer direct je contactpersoon. Hierdoor geef je de opdrachtgever toegang tot beperkte gegevens binnen je project.</p>
                                    <q-dropdown
                                        v-if="searchContactPersonBy === 'registered-users'"
                                        v-model="chosenContactPerson"
                                        :options="organisationUsers"
                                        :disabled="selectOnly"
                                        avatars
                                        placeholder="Selecteer gebruiker"
                                        class="choose-contact-person"
                                        style="width: 280px;"
                                        autocomplete
                                    ></q-dropdown>
                                    <q-input
                                        v-if="searchContactPersonBy === 'email'"
                                        v-model="contactPersonEmail"
                                        placeholder="Voer een e-mailadres in"
                                        class="choose-contact-person"
                                        style="width: 280px;"
                                        autofocus
                                        >E-mailadres</q-input
                                    >
                                    <p class="search-other" v-if="searchContactPersonBy === 'registered-users'" @click="searchContactPersonBy = 'email'">Of nodig contactpersoon uit via e-mail</p>
                                    <p class="search-other" v-if="searchContactPersonBy === 'email'" @click="searchContactPersonBy = 'registered-users'">Of zoek geregistreerde gebruikers</p>
                                </div> 
                            </label>
                        </q-tooltip>
                    </div>

                    <div class="invite-pd-client-button">
                        <q-button :loading="loadingSave" @click="save">Opdrachtgever {{ pdInviteClientType === 'invite' ? 'uitnodigen' : 'selecteren' }}</q-button>
                    </div>
                </div>
            </div>
        </div>

        <!-- STEP 2 - CHOOSE CONTACTPERSON -->
        <div v-else-if="step === 2">
            <div class="search">
                <div class="panel">
                    <div class="title">
                        <div>
                            <span class="label">Stap 2</span>
                        </div>
                        <h3>Gekozen {{ $t(organisationType.toLowerCase()) }}</h3>
                    </div>
                    <div class="chosen mb-m">
                        <h2>{{ chosenOrganisation.name }}</h2>
                    </div>
                    <div class="input">
                        <div class="input-label">
                            Kies contactpersoon
                        </div>
                        <div v-if="searchContactPersonBy === 'registered-users'">
                            <q-dropdown
                                v-model="chosenContactPerson"
                                :options="organisationUsers"
                                avatars
                                placeholder="Selecteer gebruiker..."
                                class="choose-contact-person"
                                style="width: 280px;"
                                autocomplete
                            ></q-dropdown>

                            <p class="search-other" @click="searchContactPersonBy = 'email'">
                                Of nodig contactpersoon uit via e-mail
                            </p>
                        </div>
                        <div v-else-if="searchContactPersonBy === 'email'">
                            <q-input labelPosition="inside"
                                v-model="contactPersonEmail"
                                placeholder="Voer een e-mailadres in"
                                class="choose-contact-person"
                                style="width: 280px;"
                                autofocus
                                >E-mailadres</q-input
                            >

                            <p
                                class="search-other"
                                @click="searchContactPersonBy = 'registered-users'"
                                v-if="canSearchByRegisteredUsers"
                            >
                                Of zoek geregistreerde gebruikers
                            </p>
                        </div>

                        <div class="select mt-m">
                            <p class="mb-m" style="width: 130%;">
                                Als dit de correcte opdrachtnemer is, klik hieronder op Opdrachtnemer koppelen om de
                                opdrachtnemer te informeren. Let op, de contactpersoon krijgt hier van bericht en het
                                project is direct inzichtelijk voor {{ chosenOrganisation.name }}.
                            </p>

                            <div class="buttons">
                                <q-button :loading="loadingSave" @click="save">{{ $t(organisationType) }} koppelen</q-button>
                                <q-button @click="changeOrganisation" variation="blank" class="cancel"
                                    >Ik heb me vergist, opnieuw opzoeken</q-button
                                >
                            </div>

                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import _ from 'lodash';

import {
    KVK_COMPANIES,
    ORGANISATIONS_MINIMAL,
    GET_ORGANISATION_USERS,
    GET_ORGANISATION_BRANCHES
} from '../../../graphql/queries';
import { validateEmail } from '../../../assets/js/utils';



import ArrowButton from '@/components/ArrowButton';
import ShowMoreButton from './ShowMoreButton';
import gql from 'graphql-tag';

export default {
    name: 'q-select-organisation-crow',
    components: {
        'arrow-button': ArrowButton,
        'show-more-button': ShowMoreButton
    },
    props: {
        project: {
            type: Object,
            required: true
        },
        indicator: {
            type: Object,
            required: true
        },
        isPdProject: {
            type: Boolean,
            required: false,
            default: () => false
        },
        type: {
            type: String,
            required: true
        },
        organisationType: {
            type: String,
            required: true
        },
        indicator: {
            type: Object,
            required: true
        }
    },
    data() {
        return {
            input: '',
            previousInput: '',
            branches: [],
            kvkOrganisations: [],
            qfactOrganisations: [],
            mainColumns: [
                {
                    field: 'name',
                    label: 'Organisatie',
                    width: '100%'
                },
                {
                    field: 'type',
                    label: 'Type',
                    width: '180px',
                    filter: true,
                    filterType: 'checkbox',
                    showSearchInput: false,
                    disableSorting: true,
                    filterOptions: [
                        { value: 'hoofdvestiging', label: 'Hoofdvestiging', selected: true },
                        { value: 'nevenvestiging', label: 'Nevenvestiging', selected: true },
                        { value: 'rechtspersoon', label: 'Rechtspersoon', selected: true }
                    ],
                    tooltipText: 'Filter op type: hoofdvestiging, nevenvestiging en/ of rechtspersoon.'
                },
                {
                    field: 'kvk',
                    label: 'KvK',
                    width: '140px'
                },
                
                {
                    field: 'source',
                    label: '',
                    width: '60px'
                },
                {
                    field: 'action',
                    label: '',
                    width: '120px',
                    align: 'right'
                }
            ],
            branchColumns: [
                {
                    field: 'name',
                    label: 'Vestiging',
                    width: '260px'
                },
                {
                    field: 'address',
                    label: 'Adres',
                    width: '160px'
                },
                {
                    field: 'zip',
                    label: 'Postcode',
                    width: '120px'
                },
                {
                    field: 'city',
                    label: 'Plaats'
                },
                {
                    field: 'action',
                    label: '',
                    width: '120px',
                    align: 'right'
                }
            ],
            loading: true,
            chosenOrganisation: null,
            chosenContactPerson: null,
            organisationUsers: [],
            searchContactPersonBy: 'registered-users',
            contactPersonEmail: '',
            canSearchByRegisteredUsers: false,
            pdInviteClientType: 'silent',
            loadingSave: false,
            selectOnly: false,
            currentFilters: {},
            page: 1,
            resultsPerPage: 100,
            hasMoreResults: false,
            loadingMore: false
        };
    },
    methods: {
        selectInviteClientType(type) {
            this.pdInviteClientType = type;
        },
        save() {
            const isFromKvk = this.chosenOrganisation && this.chosenOrganisation.kvk && !this.chosenOrganisation.id;

            if (this.isPdProject) {
                const silentInvite = this.pdInviteClientType === 'silent';

                if (!silentInvite) {
                    if (this.searchContactPersonBy === 'email' && !validateEmail(this.contactPersonEmail)) {
                        this.$store.commit('notify', { type: 'danger', message: 'E-mail is niet valide' });
                        return;
                    } else if (this.searchContactPersonBy === 'registered-users' && !this.chosenContactPerson) {
                        this.$store.commit('notify', { type: 'danger', message: 'Kies een contact persoon' });
                        return;
                    }
                }

                if (isFromKvk) {

                    let email = this.contactPersonEmail;

                    if (silentInvite)
                        email = 'helpdesk@qfact.nl'

                    this.loadingSave = true;
                    this.$emit('saveFromKvk', this.chosenOrganisation, email);
                } else {
                    if (!silentInvite) {
                        if (this.searchContactPersonBy === 'registered-users')
                            this.$emit('saveContactPerson', this.chosenContactPerson, undefined, this.chosenOrganisation.id);
                        else if (this.searchContactPersonBy === 'email')
                            this.$emit('saveContactPerson', undefined, this.contactPersonEmail, this.chosenOrganisation.id);
                    }

                    this.loadingSave = true;
                    const isPrivate = this.isPdProject ? this.pdInviteClientType === 'silent' : false;
                    this.$emit('saveOrganisation', this.chosenOrganisation.id, isPrivate);
                }

            } else {
                if (this.searchContactPersonBy === 'email' && !validateEmail(this.contactPersonEmail)) {
                    this.$store.commit('notify', { type: 'danger', message: 'E-mail is niet valide' });
                    return;
                } else if (this.searchContactPersonBy === 'registered-users' && !this.chosenContactPerson) {
                    this.$store.commit('notify', { type: 'danger', message: 'Kies een contact persoon' });
                    return;
                }
                
                if (isFromKvk) {
                    this.loadingSave = true;
                    this.$emit('saveFromKvk', this.chosenOrganisation, this.contactPersonEmail);
                } else {
                    if (this.searchContactPersonBy === 'registered-users')
                        this.$emit('saveContactPerson', this.chosenContactPerson, undefined, this.chosenOrganisation.id);
                    else if (this.searchContactPersonBy === 'email')
                        this.$emit('saveContactPerson', undefined, this.contactPersonEmail, this.chosenOrganisation.id);

                    this.loadingSave = true;
                    this.$emit('saveOrganisation', this.chosenOrganisation.id);
                }
            }
        },
        _search: _.debounce(function() {
            return this.search();
        }, 500),
        search() {
            if (this.type == "main" && !this.input.trim() ) {
                this.kvkOrganisations = [];
                this.qfactOrganisations = [];
                this.previousInput = '';
                this.loading = false;
                return;
            }
            switch (this.type) {
                case 'main':
                    this.getMainOrganisations(true);
                    break;
                case 'branch':
                    this.getBranchOrganisations();
            }
            this.previousInput = this.input;
        },
        select(organisation) {
            if (this.type === 'branch') {
                this.$emit('saveOrganisation', organisation.id);
            } else {
                this.chosenOrganisation = organisation;
                this.getOrganisationUsers(organisation.id);
            }
        },
        formatKvkOrganisations(items) {
            return items.map(branch => {
                const city = branch.addresses[0].city
                return {
                    id: branch.mainExists.id,
                    name: branch.tradeNames.businessName,
                    kvk: branch.kvkNumber,
                    address: `${branch.addresses[0].street} ${branch.addresses[0].houseNumber}`,
                    code: branch.addresses[0].postalCode,
                    city: city,
                    source: 'kvk',
                    isMainBranch: branch.isMainBranch,
                    isBranch: branch.isBranch,
                    vestiging: `${branch.branchNumber}${city ? `, ${city}` : ''}`, 
                };
            });
        },
        setQfactOrganisations(organisations) {
            this.qfactOrganisations = organisations.map(org => {
                const city = org.childs[0] && org.childs[0].address ? org.childs[0].address.city : '';
                return {
                    id: org.id,
                    logo: org.logo,
                    name: org.name,
                    kvk: org.legal ? org.legal.registrationId : '',
                    city,
                    source: 'qfact',
                    isMainBranch: true,
                    isBranch: false, 
                 
                };
            });
        },
        async fetchKvkCompanies(term, page = 1, typeFilters = []) {
            try {
                return await this.$apollo.query({
                query: KVK_COMPANIES,
                variables: {
                    term,
                    includeBranches: false,
                    page: page,
                    type: typeFilters
                }, 
                fetchPolicy: 'no-cache'
                })
                .then(response => {
                    return response.data.kvk_companies;
                });
            } catch (error) {
                console.log(error)
            }
        },
        async getMainOrganisations(resetPagination = true) {
            const newInputStartWithPreInput = this.input.startsWith(this.previousInput);
            if (resetPagination) {
                this.page = 1;
            }
            
            this.loading = true;
            const notMyOrganisationType = this.indicator.masterId;
            
            const typeFilters = this.getTypeFiltersFromCurrentFilters();
            
            try {
                const showQfactCompanies = this.shouldShowQfactCompanies(typeFilters);
                
                const promises = [
                    this.fetchKvkCompanies(this.input, this.page, typeFilters)
                ];
                
                // Only fetch QFact organisations if Hoofdvestiging filter is selected
                if (showQfactCompanies) {
                    promises.push(
                        this.$apollo
                        .query({
                            query: ORGANISATIONS_MINIMAL,
                            variables: {
                                where: {
                                    AND: [
                                        {
                                            OR: [{ name__contains: this.input }, { legal_registrationId__contains: this.input }]
                                        },
                                        { type: 'main' },
                                        { products__some: { slug__contains: notMyOrganisationType } }
                                    ]
                                },
                                caseSensitive: false
                            }, 
                            fetchPolicy: 'no-cache'
                        })
                        .then(response => response.data.organisations)
                    );
                } else {
                    promises.push(Promise.resolve([]));
                }
                
                const [kvkCompanies, qfactOrganisations] = await Promise.all(promises);
                let kvkOrganisations = []
                if (showQfactCompanies) {
                    this.setQfactOrganisations(qfactOrganisations);
                    
                    const qfactKvkNumbers = qfactOrganisations.map(org => org.legal ? org.legal.registrationId : null).filter(org => org !== null);
                    const filteredCompanies = kvkCompanies.filter(company => !qfactKvkNumbers.includes(company.kvkNumber));
                    
                    const groupedCompanies = this.groupKvkCompaniesByPriority(filteredCompanies);
                    kvkOrganisations = this.formatKvkOrganisations(groupedCompanies);
                } else {
                    this.qfactOrganisations = [];
                    const groupedCompanies = this.groupKvkCompaniesByPriority(kvkCompanies);
                    kvkOrganisations = this.formatKvkOrganisations(groupedCompanies);
                }
                
                if(newInputStartWithPreInput && this.kvkOrganisations.length ){
                    const uniqueNewOrganisations = this.filterDuplicatesByKvkNumber(kvkOrganisations, this.kvkOrganisations);
                    const allOrganisation =  [...this.kvkOrganisations , ...uniqueNewOrganisations];
                    if(typeFilters.length){
                        const filteredCompanies = allOrganisation.filter(org => {
                            let typeValue = 'rechtspersoon'; 
                            if (org.isMainBranch) typeValue = 'hoofdvestiging';
                            else if (org.isBranch) typeValue = 'nevenvestiging';
                            return typeFilters.includes(typeValue)
                        });
                        this.kvkOrganisations = filteredCompanies
                    } else {
                        this.kvkOrganisations  = allOrganisation
                    }
                        
                
            
                }
                else {
                    this.kvkOrganisations = kvkOrganisations
                }
                console.log(this.kvkOrganisations)
                this.hasMoreResults = kvkCompanies && kvkCompanies.length === this.resultsPerPage;
                this.loading = false;
                this.loadingMore = false;
            } catch(error) {
                console.error(error);
                this.loading = false;
                this.loadingMore = false;
            }
        },
        getBranchOrganisations() {
            this.loading = true;
            const masterId = 'contractor-branch';
            const contractorId = masterId === 'contractor-branch' ? this.project.contractorId : null;
            const clientId = masterId === 'client-branch' ? this.project.clientId : null;

            if (!clientId && !contractorId) return console.error('no contractor or client id set');

            const organisationId = contractorId ? contractorId : clientId;

            this.$apollo
                .query({
                    query: GET_ORGANISATION_BRANCHES,
                    variables: { organisationId }, 
                    fetchPolicy: 'no-cache'
                })
                .then(response => {
                    this.setBranchOrganisations(response.data.organisation);
                    this.loading = false;
                })
                .catch(err => {
                    console.log(err);
                    this.loading = false;
                });
        },
        setBranchOrganisations(organisation) {
            this.branches = organisation.childs.map(branch => {
                return {
                    id: branch.id,
                    logo: organisation.logo,
                    name: branch.name,
                    address: branch.address.line1 ? branch.address.line1.replace(/null/g, "") : branch.address.line1,
                    zip: branch.address.zip ? branch.address.zip.replace(/null/g, "") : branch.address.zip,
                    city: branch.address.city ? branch.address.city.replace(/null/g, "") : branch.address.city,
                    source: 'qfact'
                };
            });
        },
        back() {
            this.$parent.cancel();
        },
        getOrganisationUsers(organisationId) {
            if (!organisationId) {
                this.searchContactPersonBy = 'email';
                this.canSearchByRegisteredUsers = false;
                return;
            } else {
                this.canSearchByRegisteredUsers = true;
                this.searchContactPersonBy = 'registered-users';
            }
            this.$apollo
                .query({
                    query: GET_ORGANISATION_USERS,
                    variables: { organisationId }
                })
                .then(result => {
                    if (!result.data.organisation) return;

                    this.organisationUsers = result.data.organisation.users
                        .filter(user => user.status === 'active')
                        .map(user => {
                            return {
                                value: user.user.id,
                                label: `${user.user.firstName} ${user.user.lastName}`,
                                avatar: user.user.avatar,
                                avatarFallback:
                                    user.user.firstName && user.user.lastName
                                        ? `${user.user.firstName.charAt(0)}${user.user.lastName.charAt(0)}`
                                        : '?'
                            };
                        });
                })
                .catch(err => {});
        },
        changeOrganisation() {
            this.chosenOrganisation = null;
            this.chosenContactPerson = null;
            this.contactPersonEmail = '';
            this.searchContactPersonBy = 'registered-users';
        },
        handleFilterUpdate(filters) {
            this.currentFilters = filters;
            this.search();
        },
        async showMoreResults() {
            try {
                this.loadingMore = true;
                this.page = this.page + 1;
                
                const typeFilters = this.getTypeFiltersFromCurrentFilters();
                
                const kvkCompanies = await this.fetchKvkCompanies(this.input, this.page, typeFilters);
                const showQfactCompanies = this.shouldShowQfactCompanies(typeFilters);
                
                let filteredCompanies;
                if (showQfactCompanies) {
                    const qfactKvkNumbers = this.qfactOrganisations.map(org => org.legal ? org.legal.registrationId : null).filter(org => org !== null);
                    filteredCompanies = kvkCompanies.filter(company => !qfactKvkNumbers.includes(company.kvkNumber));
                } else {
                    filteredCompanies = kvkCompanies;
                }
                    
                const groupedCompanies = this.groupKvkCompaniesByPriority(filteredCompanies);
                const newKvkOrganisations = this.formatKvkOrganisations(groupedCompanies);
                
                const uniqueNewOrganisations = this.filterDuplicatesByKvkNumber(newKvkOrganisations, this.kvkOrganisations);
                this.kvkOrganisations = [...this.kvkOrganisations, ...uniqueNewOrganisations];
                    
                this.hasMoreResults = kvkCompanies && kvkCompanies.length === this.resultsPerPage;
                this.loadingMore = false;
            }
            catch(err){
                 this.loadingMore = false;
                 this.page = this.page - 1;
            }
        },
        groupKvkCompaniesByPriority(organisations) {
            if (!organisations || organisations.length === 0) {
                return [];
            }

            const groups = {};
            organisations.forEach(organisation => {
                const kvkNumber = organisation.kvkNumber;
                if (!groups[kvkNumber]) {
                    groups[kvkNumber] = [];
                }
                groups[kvkNumber].push(organisation);
            });

            const selectedOrganisations = [];
            Object.values(groups).forEach(group => {
                let selected = null;
                
                selected = group.find(org => org.isMainBranch === true);
                
                if (!selected) {
                    selected = group.find(org => org.isBranch === true);
                }
                if (!selected) {
                    selected = group[0];
                }
                
                selectedOrganisations.push(selected);
            });

            return selectedOrganisations;
        },
        removeDuplicates(organisations) {
            let uniqueOrganisations = [];
            
            
            if (typeof organisations === 'object' && organisations.length > 0) {
                organisations.forEach(organisation => {
                    if (!uniqueOrganisations.find(org => org.kvkNumber === organisation.kvkNumber)) {
                        uniqueOrganisations.push(organisation);
                    }
                });
            }

            return uniqueOrganisations
        },
        getTypeFiltersFromCurrentFilters() {
            if (!this.currentFilters || !this.currentFilters.type) {
                return [];
            }
            const typeFilter = this.currentFilters.type.filter;
            if (Array.isArray(typeFilter)) {
                return typeFilter;
            }
            
            return [];
        },
        shouldShowQfactCompanies(typeFilters) {
            return typeFilters.length ? typeFilters.includes('hoofdvestiging') : true;
        },
        filterDuplicatesByKvkNumber(newOrganisations, existingOrganisations) {
            if (!newOrganisations || newOrganisations.length === 0) {
                return [];
            }
            
            const existingKvkNumbers = new Set(existingOrganisations.map(org => org.kvk));
            return newOrganisations.filter(org => !existingKvkNumbers.has(org.kvk));
        }
       
    },
    computed: {
        step: function() {
            const isInitiator = this.project.organisationId === this.$store.getters.getCurrentOrganisation.id;
            this.selectOnly = isInitiator && (!this.project.usesCrowFlow || !this.$store.getters.hasActiveQfactProducts);
            if (this.chosenOrganisation && this.isPdProject) return '2a';
            else if (this.chosenOrganisation) return 2;
            else return 1;
        },
        getColumns: function() {
            switch (this.type) {
                case 'main':
                    return this.mainColumns;
                    break;
                case 'branch':
                    return this.branchColumns;
                    break;
                default:
                    break;
            }
        },
        organisations() {
            const allOrgs = this.type === 'main' ? [...this.qfactOrganisations, ...this.kvkOrganisations] : this.branches;
            
            return allOrgs.map(org => {
                let typeValue = 'rechtspersoon'; // default
                if (org.isMainBranch) typeValue = 'hoofdvestiging';
                else if (org.isBranch) typeValue = 'nevenvestiging';
                
                return {
                    ...org,
                    type: typeValue
                };
            });
        }
    },
    created() {
        if (this.type === 'branch') this.search();
    }
};
</script>

<style lang="scss" scoped>
@import '../assets/style/_variables.scss';
@import '../assets/style/fonts/fonts.css';

.radio {
    margin-top: 4px;
}

.select {
    width: 115%;

    .buttons {
        display: flex;
        align-items: center;
        justify-content: space-between;
    }

    .cancel {
        margin-left: 24px;
    }
}
.type {
    display: flex;
    align-items: center;
    gap: 15px;
}

.search-other {
    color: $color-grey-7;
    cursor: pointer;
    text-decoration: underline;
}

.choose-contact-person {
    width: 60%;
    padding: 10px 0;
}

.chosen {
    h2 {
        font-weight: 500;
        font-size: 23px;
        line-height: 23px;
        margin: 20px 0;
    }
}

.head {
    margin-bottom: 48px;
    display: flex;
    justify-content: space-between;

    .left {
        display: flex;
        align-items: center;

        .status {
            margin-left: 10px;
        }

        h2 {
            font-weight: 500;
            font-size: 23px;
            line-height: 23px;
        }
    }
}

.results {
    display: flex;
    justify-content: center;
    margin-top: 50px;

    & .edit {
        justify-content: right !important;
    }
}

.input-label {
    font-weight: 500;
    font-size: 14px;
    line-height: 24px;
    margin-top: 15px;
}

.search {
    display: flex;
    justify-content: center;

    margin-top: 150px;

    .panel {
        width: 460px;

        .title {
            display: flex;
            align-items: center;
            margin-left: -64px;

            h3 {
                margin-left: 24px;
            }
        }
    }
}

.search-pd {
    display: flex;
    justify-content: center;
    align-items: center;

    .panel {
        width: 460px;

        .title {
            display: flex;
            align-items: center;
            margin-left: -64px;

            h3 {
                margin-left: 24px;
            }
        }
    }
}

.label {
    font-family: Gotham;
    font-style: normal;
    font-weight: 500;
    font-size: 10px;
    line-height: 16px;
    background: #f2f2f2;
    border: 1px solid #dee2e6;
    border-radius: 4px;
    color: $color-grey-7;
    padding: 4px;
}

.q-select-organisation {
    position: absolute;
    left: 0;
    right: 0;
    bottom: 0;
    top: 1px;

    z-index: 20;
    background-color: white;
}

h3 {
    font-weight: 500;
    font-size: 19px;
    line-height: 24px;
}

.not-found-text {
    text-align: center;
    font-weight: 500;
    color: #aeb5bb;
}

.option-panel {
    background: #F5F5F5;
    border-radius: 3px;
    padding: 15px;
    display: flex;
    cursor: pointer;

    &.disabled {
        pointer-events: none;
        opacity: 0.6;
    }
}
.invite-pd-client-button {
    width: 100%;
    margin: 93px 0 0 0;

    display: flex;
    justify-content: flex-end;
}
</style>
